// Hook de gravação do celular: getUserMedia -> MediaRecorder, cronômetro e um
// AnalyserNode que alimenta a onda ao vivo (prova de captação real, não decorativa).
//
// Interrupção do sistema (aba perde foco / tela bloqueia / entra ligação): AUTO-PAUSA
// e sinaliza `interrompido` — decisão do piloto: nunca gravar silêncio calado, sempre
// preservar o que já foi captado. iOS Safari suspende a captação em segundo plano; a
// track de áudio emite 'mute' e o document dispara 'visibilitychange'. ESTE CAMINHO
// PRECISA DE TESTE EM iPHONE REAL — não é validável no ambiente automatizado (sem
// dispositivo). Ver o relatório ao usuário.

import { useCallback, useEffect, useRef, useState, type MutableRefObject } from 'react';

export type FaseGravacao = 'repouso' | 'gravando' | 'pausado';

// Preferência: webm/opus (Android/Chrome). iOS não suporta webm — retornamos undefined
// e o MediaRecorder cai no default do browser (audio/mp4). upload.ts aceita ambos e o
// ffmpeg (normalize.ts, `-i` sem assumir formato) normaliza os dois.
function escolherMime(): string | undefined {
  const cands = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4'];
  const suportado = (t: string): boolean =>
    typeof MediaRecorder !== 'undefined' &&
    typeof MediaRecorder.isTypeSupported === 'function' &&
    MediaRecorder.isTypeSupported(t);
  for (const c of cands) if (suportado(c)) return c;
  return undefined;
}

export interface Gravador {
  fase: FaseGravacao;
  segundos: number;
  interrompido: boolean;
  erroMic: string | null;
  analyser: MutableRefObject<AnalyserNode | null>;
  iniciar: () => Promise<void>;
  pausar: () => void;
  retomar: () => void;
  encerrar: () => Promise<Blob | null>;
  descartar: () => void;
}

export function useGravador(): Gravador {
  const [fase, setFase] = useState<FaseGravacao>('repouso');
  const [segundos, setSegundos] = useState(0);
  const [interrompido, setInterrompido] = useState(false);
  const [erroMic, setErroMic] = useState<string | null>(null);

  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const analyser = useRef<AnalyserNode | null>(null);
  const timerRef = useRef<number | null>(null);
  const faseRef = useRef<FaseGravacao>('repouso'); // leitura síncrona dentro de handlers

  const setFaseSync = useCallback((f: FaseGravacao) => {
    faseRef.current = f;
    setFase(f);
  }, []);

  const pararTimer = useCallback(() => {
    if (timerRef.current !== null) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const iniciarTimer = useCallback(() => {
    pararTimer();
    timerRef.current = window.setInterval(() => setSegundos((s) => s + 1), 1000);
  }, [pararTimer]);

  const limparRecursos = useCallback(() => {
    pararTimer();
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    if (audioCtxRef.current && audioCtxRef.current.state !== 'closed') {
      void audioCtxRef.current.close();
    }
    audioCtxRef.current = null;
    analyser.current = null;
    recorderRef.current = null;
  }, [pararTimer]);

  const pausar = useCallback(() => {
    const rec = recorderRef.current;
    if (rec && rec.state === 'recording') {
      rec.pause();
      pararTimer();
      setFaseSync('pausado');
    }
  }, [pararTimer, setFaseSync]);

  const retomar = useCallback(() => {
    const rec = recorderRef.current;
    if (rec && rec.state === 'paused') {
      rec.resume();
      iniciarTimer();
      setInterrompido(false);
      setFaseSync('gravando');
    }
  }, [iniciarTimer, setFaseSync]);

  const iniciar = useCallback(async () => {
    setErroMic(null);
    if (!navigator.mediaDevices?.getUserMedia) {
      setErroMic(
        'Este navegador não permite gravar áudio aqui (precisa de HTTPS). Use "enviar um arquivo" abaixo.',
      );
      return;
    }

    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      setErroMic(
        'Não foi possível acessar o microfone. Autorize o acesso ao microfone e tente de novo.',
      );
      return;
    }
    streamRef.current = stream;

    // Onda ao vivo: AnalyserNode ligado ao stream REAL do microfone.
    try {
      const Ctx =
        window.AudioContext ??
        (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (Ctx) {
        const ctx = new Ctx();
        audioCtxRef.current = ctx;
        const src = ctx.createMediaStreamSource(stream);
        const an = ctx.createAnalyser();
        an.fftSize = 1024;
        an.smoothingTimeConstant = 0.6;
        src.connect(an);
        analyser.current = an;
      }
    } catch {
      // A onda é feedback; sua ausência não impede gravar.
    }

    const mime = escolherMime();
    const rec = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
    chunksRef.current = [];
    rec.ondataavailable = (ev) => {
      if (ev.data && ev.data.size > 0) chunksRef.current.push(ev.data);
    };
    recorderRef.current = rec;
    rec.start(1000); // timeslice: flush periódico, não perde áudio se interromper

    setSegundos(0);
    setInterrompido(false);
    iniciarTimer();
    setFaseSync('gravando');

    // Interrupção do SO (iOS): a track emite 'mute' quando a captação é suspensa.
    const track = stream.getAudioTracks()[0];
    track?.addEventListener('mute', () => {
      if (faseRef.current === 'gravando') {
        setInterrompido(true);
        pausar();
      }
    });
  }, [iniciarTimer, pausar, setFaseSync]);

  const encerrar = useCallback(async (): Promise<Blob | null> => {
    const rec = recorderRef.current;
    if (!rec) return null;
    const tipo = rec.mimeType || 'audio/webm';
    const blob = await new Promise<Blob>((resolve) => {
      rec.onstop = () => resolve(new Blob(chunksRef.current, { type: tipo }));
      if (rec.state !== 'inactive') rec.stop();
      else resolve(new Blob(chunksRef.current, { type: tipo }));
    });
    limparRecursos();
    setFaseSync('repouso');
    return blob.size > 0 ? blob : null;
  }, [limparRecursos, setFaseSync]);

  const descartar = useCallback(() => {
    const rec = recorderRef.current;
    if (rec && rec.state !== 'inactive') {
      rec.onstop = null;
      rec.stop();
    }
    chunksRef.current = [];
    limparRecursos();
    setSegundos(0);
    setInterrompido(false);
    setFaseSync('repouso');
  }, [limparRecursos, setFaseSync]);

  // Aba perde foco enquanto grava -> auto-pausa + aviso (defensivo p/ iOS/PWA).
  useEffect(() => {
    function aoVisibilidade(): void {
      if (document.hidden && faseRef.current === 'gravando') {
        setInterrompido(true);
        pausar();
      }
    }
    document.addEventListener('visibilitychange', aoVisibilidade);
    return () => document.removeEventListener('visibilitychange', aoVisibilidade);
  }, [pausar]);

  // Desmontagem: solta microfone e AudioContext.
  useEffect(() => () => limparRecursos(), [limparRecursos]);

  return {
    fase,
    segundos,
    interrompido,
    erroMic,
    analyser,
    iniciar,
    pausar,
    retomar,
    encerrar,
    descartar,
  };
}
